import React from 'react'
import { Button, IconButton } from '@mui/material'
import { LanguageRounded } from '@mui/icons-material'
import useContentfulABTest from '@talentinc/gatsby-theme-ecom/hooks/useContentfulABTest'
import NavigationGroupLink from '../NavigationGroup/NavigationGroupLink'
import ModalLaunchButton from '../Modals/ModalLaunchButton'
import {
  Button as ButtonType,
  ActionLink,
  ButtonRole,
  ExternalLink,
} from '../../types/page'
import { Modal } from '../../types/modal'
import { ABTest, ButtonActionABTest } from '../../types/abTest'
import PurchaseButton from '../NavigationGroup/PurchaseButton'
import usePageProduct from '../../hooks/usePageProduct'
import { MatchClasses } from '../../types/contentfulAttributes'
import useSiteMetadata from '../../hooks/useSiteMetadata'
import useUser from '../../hooks/useUser'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'
import insertHandlebarsTemplate from '../../utils/insertHandlebarsTemplate'
import { ContentfulType } from '../../types/references'
import TRResumeUpload from '../../components/Widgets/TRResumeUpload'
import { useMixpanelCTAClickTracking } from '../../utils/mixpanel'
import useTidyLink from '../../hooks/useTidyLink'

export interface NavigationButtonProps {
  link:
    | ButtonType<ActionLink>
    | ButtonType<Modal>
    | ButtonType<ABTest<ButtonActionABTest>>
  className?: string
  onClick?: React.MouseEventHandler | (() => void)
}

const NavigationButton: React.FC<NavigationButtonProps> = ({
  link,
  className,
  onClick,
}) => {
  const pageSku = usePageProduct()
  const handlebarsContext = useHandlebarsContext()
  const parsedButtonText = insertHandlebarsTemplate(
    link.buttonText,
    handlebarsContext
  )
  const {
    isLoading,
    lead_auth_token: leadAuthToken,
    viewable_critique: viewableCritique,
  } = useUser()
  const { defaultResumeProduct } = useSiteMetadata()
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()
  const tidyTheLink = useTidyLink()

  if (!link.action) {
    if (link.role) {
      if (link.role === ButtonRole.OpenIntercom) {
        return (
          <Button
            className={className}
            color={link.color || 'primary'}
            variant={link.variant || 'contained'}
          >
            {link.buttonText}
          </Button>
        )
      }

      return (
        <Button
          className={className}
          color={link.color || 'primary'}
          variant={link.variant || 'contained'}
          onAuxClick={(e) => {
            if (link.onClick) {
              link.onClick(e)
            } else if (onClick) {
              onClick(e)
            }
            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              // @ts-ignore
              tidyTheLink(link.action.slug ?? link.action.url),
              link.mixpanelTracking
            )
          }}
          onClick={(e) => {
            if (link.onClick) {
              link.onClick(e)
            } else if (onClick) {
              onClick(e)
            }
            handleMpCTAClickTracking(
              e.currentTarget.textContent,
              // @ts-ignore
              tidyTheLink(link.action.slug ?? link.action.url),
              link.mixpanelTracking
            )
          }}
        >
          {parsedButtonText}
        </Button>
      )
    }

    return null
  }

  if (link.action.__typename === 'ContentfulModal') {
    if (link.role === ButtonRole.LanguageSwitcher && onClick) {
      return (
        <IconButton
          id="regionsBtnMobile"
          className={className}
          color={link.color || 'primary'}
          onClick={onClick}
          size="large"
        >
          {parsedButtonText}
          <LanguageRounded />
        </IconButton>
      )
    }
    return (
      <ModalLaunchButton
        onClick={onClick}
        button={link as ButtonType<Modal>}
        className={className}
      />
    )
  }

  if (link.action.__typename === 'ContentfulABTest') {
    return (
      <ABTestNavigationButton
        link={link as ButtonType<ABTest<ButtonActionABTest>>}
        className={className}
      />
    )
  }

  if (
    link.action.__typename === 'ContentfulExternalLink' &&
    link.action.url.includes('purchase')
  ) {
    return (
      <PurchaseButton
        link={link as ButtonType<ExternalLink>}
        className={className}
        pageSKU={pageSku}
      />
    )
  }

  if (
    className?.includes(MatchClasses.ReviewBtn) &&
    !isLoading &&
    leadAuthToken
  ) {
    const getURL = (isCritiqueViewable: boolean | undefined) => {
      switch (isCritiqueViewable) {
        case true:
          return `/${defaultResumeProduct.toLowerCase()}critiqueview?at=${leadAuthToken}`

        default:
          return `/account-history?at=${leadAuthToken}`
      }
    }

    const modifiedBtnUrl = getURL(viewableCritique)

    const modifiedLink = {
      ...link,
      action: {
        ...link.action,
        url: modifiedBtnUrl,
      },
    }

    return (
      // @ts-ignore
      <Button
        className={className}
        color={modifiedLink.color || 'primary'}
        variant={modifiedLink.variant || 'contained'}
        component={React.forwardRef<HTMLAnchorElement>((props, innerRef) => (
          <NavigationGroupLink
            link={{
              text: parsedButtonText,
              // @ts-ignore
              action: modifiedLink.action,
              // @ts-ignore
              contentful_id: modifiedLink.action.contentful_id,
              // @ts-ignore
              __typename: modifiedLink.action.__typename,
            }}
            {...props}
            ref={innerRef}
          />
        ))}
        // @ts-ignore
        onAuxClick={(e) => {
          if (onClick) {
            onClick(e)
          }

          handleMpCTAClickTracking(
            e.currentTarget.textContent,
            modifiedLink.action.url,
            modifiedLink.mixpanelTracking
          )
        }}
        // @ts-ignore
        onClick={(e) => {
          if (onClick) {
            onClick(e)
          }

          handleMpCTAClickTracking(
            e.currentTarget.textContent,
            modifiedLink.action.url,
            modifiedLink.mixpanelTracking
          )
        }}
      />
    )
  }

  if (link.action.__typename === ContentfulType.ResumeUpload) {
    return <TRResumeUpload options={link.action} />
  }

  return (
    // @ts-ignore
    <Button
      className={className}
      color={link.color || 'primary'}
      variant={link.variant || 'contained'}
      component={React.forwardRef<HTMLAnchorElement>((props, innerRef) => {
        return (
          <NavigationGroupLink
            link={{
              text: parsedButtonText,
              // @ts-ignore
              action: link.action,
              // @ts-ignore
              contentful_id: link.action.contentful_id,
              // @ts-ignore
              __typename: link.action.__typename,
            }}
            {...props}
            ref={innerRef}
          />
        )
      })}
      // @ts-ignore
      onAuxClick={(e) => {
        if (onClick) {
          onClick(e)
        }

        handleMpCTAClickTracking(
          e.currentTarget.textContent,
          tidyTheLink(
            // @ts-ignore
            link.action.slug ?? link.action.url ?? `#${link.action.hash}`
          ),
          link.mixpanelTracking
        )
      }}
      // @ts-ignore
      onClick={(e) => {
        if (onClick) {
          onClick(e)
        }

        handleMpCTAClickTracking(
          e.currentTarget.textContent,
          tidyTheLink(
            // @ts-ignore
            link.action.slug ?? link.action.url ?? `#${link.action.hash}`
          ),
          link.mixpanelTracking
        )
      }}
    />
  )
}

interface ABTestNavigationButtonProps
  extends Omit<NavigationButtonProps, 'link'> {
  link: ButtonType<ABTest<ButtonActionABTest>>
}

const ABTestNavigationButton: React.FC<ABTestNavigationButtonProps> = ({
  link,
  ...props
}) => {
  const action = useContentfulABTest(link.action, {
    defaultToOriginal: true,
  })

  if (!action) {
    /* eslint-disable-next-line no-console */
    console.warn(
      `Could not render action for A/B test ${link.action.googleOptimizeId}, could be an issue with the query for data`
    )
    return null
  }

  return (
    <NavigationButton
      link={{
        ...link,
        action,
      }}
      {...props}
    />
  )
}

export default NavigationButton
